/*
 * 🧹 Working-note scrubber — site-wide helper, mirrors backend/text_sanitizer.py.
 *
 * Standing rule (owner directive 2026-10-29): any player-facing page
 * must read as a finished game, never as a working document. Call this
 * on every string pulled from a `by:` / "filed-by-Roc"-style data file
 * before it reaches a React render.
 *
 * Drops working-document voice:
 *   • "FILED BY ROC" dates, "filed by Roc, 2026-09-18"
 *   • "AWAITING CANON", "Roc hasn't filed …"
 *   • "swap when new X drops", "no permission needed"
 *   • "[S1✗]" working tags
 *   • ENGINE LINES design commentary (caller must also hide the block)
 *   • bare mentions of "Roc" / "Brice" in player strings
 *
 * Keeps real content: deck sizes, counts, section names, stats.
 */

const SENTENCE_DROP_RE = new RegExp(
  [
    // bare dev-tag mentions
    "\\bRoc\\b",
    "\\bBrice\\b",
    // working verbs / headers
    "\\bAPPROVED\\b",
    "\\bFULL CANON\\b",
    "\\bAWAITING CANON\\b",
    "\\bawaiting canon\\b",
    "\\bhasn['’]?t filed\\b",
    "\\bno permission needed\\b",
    "\\bswap when new\\b",
    "\\bbackfill rotation\\b",
    "\\bfiled in Decks\\b",
    "\\bfiled by\\b",
    "\\bFull card:",
    "\\bFile:",
    "\\bFinal art\\b",
    "\\bConcept art\\b",
    "\\bLayout v\\d",
    "\\bbuilt\\s+20\\d{2}-\\d{2}-\\d{2}\\b",
    "\\bart direction\\b",
    "\\bowner tuning\\b",
    "\\bpending owner\\b",
    "\\bNumbering note\\b",
    "\\bArt\\s*:",
    "\\bLore\\s*:",
    "\\bNOTE\\s*[:(]",
    "\\bScene art\\b",
  ].join("|"),
  "i",
);

const INLINE_PAREN_RE = new RegExp(
  [
    "\\(Roc[^)]*\\)",
    "\\([^)]*approved[^)]*\\)",
    "\\(filed[^)]*\\)",
    "\\(Layout\\s*v\\d[^)]*\\)",
    "\\([^)]*\\bbuilt\\s*20\\d{2}[^)]*\\)",
    "\\(20\\d{2}-\\d{2}-\\d{2}\\)",
    "\\([^)]*Numbering note[^)]*\\)",
    "\\([^)]*\\bBrice\\b[^)]*\\)",
    "\\([^)]*\\bRoc\\b[^)]*\\)",
  ].join("|"),
  "gi",
);

const INLINE_TOKEN_RE = new RegExp(
  [
    "\\[S1[✗x]\\]",                       // [S1✗] working tag
    "`[^`]+\\.(?:webp|png|jpe?g|svg|gif)`", // backticked art paths
  ].join("|"),
  "gi",
);

const LEADING_PREFIX_RE =
  /^\s*\d+\.\s*\*\*[^*]+\*\*(?:\s*\[[^\]]+\])?\s*(?:×\s*\d+\s*)?[—–\-]?\s*/;

/**
 * Scrub working-document voice from a free-text string. Returns an
 * empty string when nothing player-safe remains (callers should hide
 * the enclosing element in that case).
 */
export function scrubWorkingNotes(text) {
  if (!text || typeof text !== "string") return text;

  // 1) Shear leading "N. **Name** [S1]" prefix (binder rules only, but
  //    cheap and safe to run on every string).
  let out = text.replace(LEADING_PREFIX_RE, "");

  // 2) Strip inline parentheticals / backticked file paths BEFORE we
  //    sentence-split so a note tucked in parens doesn't poison the
  //    whole sentence around it.
  out = out.replace(INLINE_PAREN_RE, "");
  out = out.replace(INLINE_TOKEN_RE, "");

  // 3) Drop any sentence containing a working-note marker.
  const sentences = out.split(/(?<=[.!?])\s+|\n+/);
  out = sentences.filter((s) => s.trim() && !SENTENCE_DROP_RE.test(s)).join(" ");

  // 4) Collapse residue — empty "()", "** **", stray " — .", runs.
  out = out
    .replace(/\(\s*\)/g, "")
    .replace(/\*\*\s*\*\*/g, "")
    .replace(/(?:^|\s)[—–\-]\s*(?:\.|$)/g, ".")
    .replace(/\s+([.,;:!?])/g, "$1")
    .replace(/[ \t]+/g, " ")
    .replace(/^[\s—–\-·•*]+/, "")
    .replace(/[\s—–\-*]+$/, "")
    .trim();

  return out;
}

/**
 * Return `note` scrubbed, or null if the whole thing was a working note.
 * Convenience for card-row `note` fields where the DOM wrapper
 * (`— {note}`) should disappear entirely rather than render an empty dash.
 */
export function scrubCardNote(note) {
  const cleaned = scrubWorkingNotes(note);
  if (!cleaned) return null;
  // A note that collapsed to just punctuation / separators is also empty.
  if (!/[a-z0-9]/i.test(cleaned)) return null;
  return cleaned;
}
